import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, X, Pencil, Check, Archive } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';

// Edita as opções de uma pergunta já criada: adicionar, renomear (sempre
// livre), remover (soft-delete sempre livre — se a opção já tinha voto, o
// backend mantém ela "fantasma" no resultado mesmo depois de removida daqui).
const OpcaoListEditor = ({ enqueteId, pergunta, opcoes, onChange }) => {
  const [novaOpcao, setNovaOpcao] = useState('');
  const [adicionando, setAdicionando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [textoEdicao, setTextoEdicao] = useState('');

  const opcoesAtivas = opcoes.filter((o) => o.ativa);
  const opcoesFantasma = opcoes.filter((o) => !o.ativa);

  const adicionar = async () => {
    if (!novaOpcao.trim()) return;
    setAdicionando(true);
    try {
      await adminEnquetesAPI.criarOpcao(enqueteId, pergunta.id, novaOpcao.trim());
      setNovaOpcao('');
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao adicionar opção');
    } finally {
      setAdicionando(false);
    }
  };

  const iniciarEdicao = (opcao) => {
    setEditandoId(opcao.id);
    setTextoEdicao(opcao.texto);
  };

  const salvarEdicao = async (opcao) => {
    if (!textoEdicao.trim()) return;
    try {
      await adminEnquetesAPI.atualizarOpcao(enqueteId, pergunta.id, opcao.id, textoEdicao.trim());
      setEditandoId(null);
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao editar opção');
    }
  };

  const remover = async (opcao) => {
    if (!window.confirm(`Remover a opção "${opcao.texto}" do formulário?`)) return;
    try {
      await adminEnquetesAPI.removerOpcao(enqueteId, pergunta.id, opcao.id);
      sucessoComHorario('Opção removida do formulário');
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao remover opção');
    }
  };

  return (
    <div className="mt-3 sm:pl-1 space-y-1.5">
      {opcoesAtivas.map((opcao) => (
        <div key={opcao.id} className="flex items-center gap-2 bg-gray-50 rounded-lg pl-3 pr-1.5 py-1.5">
          {editandoId === opcao.id ? (
            <>
              <input
                value={textoEdicao}
                onChange={(e) => setTextoEdicao(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && salvarEdicao(opcao)}
                className="flex-1 bg-white border border-gray-200 rounded-md px-2 py-1 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none"
                autoFocus
              />
              <button
                onClick={() => salvarEdicao(opcao)}
                className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-emerald-600 hover:bg-emerald-100 rounded-md transition-colors"
              >
                <Check className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 text-sm text-gray-700 min-w-0 truncate">{opcao.texto}</span>
              <button
                onClick={() => iniciarEdicao(opcao)}
                className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-primary-600 hover:bg-white rounded-md transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => remover(opcao)}
                className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-white rounded-md transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      ))}

      {opcoesFantasma.length > 0 && (
        <div className="space-y-1.5 pt-0.5">
          {opcoesFantasma.map((opcao) => (
            <div
              key={opcao.id}
              className="flex items-center gap-2 border border-dashed border-gray-200 rounded-lg px-3 py-1.5"
              title="Removida do formulário público, mas mantida aqui porque já recebeu voto"
            >
              <Archive className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
              <span className="flex-1 text-xs text-gray-400 italic truncate">{opcao.texto}</span>
              <span className="text-[10px] text-gray-300 flex-shrink-0 hidden sm:inline">removida · histórico preservado</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <input
          value={novaOpcao}
          onChange={(e) => setNovaOpcao(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              adicionar();
            }
          }}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all"
          placeholder="Nova opção..."
        />
        <button
          onClick={adicionar}
          disabled={adicionando || !novaOpcao.trim()}
          className="flex-shrink-0 flex items-center gap-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-40 disabled:hover:bg-transparent rounded-lg px-2.5 py-1.5 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </div>
    </div>
  );
};

export default OpcaoListEditor;
